import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MAX_SCENE_TEXT_CHARS, type SceneIntExt } from '@scoutaround/script-breakdown-shared';

import type { SceneValidationRow } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { MoreMenu } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { INT_EXT_LABEL, STATUS_LABEL, STATUS_STATE, linesWithOffsets, sceneLabel, warningText } from './sceneLabels.js';

export interface SceneEdit {
  slugline?: string;
  sceneNumber?: string | null;
  intExt?: SceneIntExt | null;
  timeOfDay?: string | null;
  text?: string | null;
}

type Mode = 'VIEW' | 'SPLIT' | 'MERGE' | 'EXCLUDE';

/** What a scene is, beside its heading: interior or exterior, the time, where its text came from. */
export function sceneFacts(scene: SceneValidationRow): string {
  return [
    scene.intExt === null ? 'Int./Ext. not stated' : (INT_EXT_LABEL[scene.intExt] ?? scene.intExt),
    scene.timeOfDay === null ? null : scene.timeOfDay.charAt(0) + scene.timeOfDay.slice(1).toLowerCase(),
    scene.textSource === null ? 'No text' : scene.textSource === 'TYPED' ? 'Typed text' : 'Screenplay text',
  ]
    .filter((part) => part !== null)
    .join(' · ');
}

/**
 * The marks a scene can carry besides its status — each a fact somebody may
 * need before confirming it, with the id the tests and the smoke walk read.
 */
export function SceneMarkers({ scene, idPrefix = 'scene' }: { scene: SceneValidationRow; idPrefix?: 'scene' | 'scene-panel' }) {
  const marks: ReactNode[] = [];
  if (scene.originalSlugline !== null && scene.originalSlugline !== scene.slugline) {
    marks.push(
      <span key="edited" className="sw-mark" title={`As written: ${scene.originalSlugline}`} data-testid={idPrefix === 'scene' ? `scene-edited-${scene.id}` : 'scene-panel-edited'}>
        Heading edited
      </span>,
    );
  }
  if (scene.origin === 'MANUAL') {
    marks.push(
      <span key="manual" className="sw-mark" data-testid={idPrefix === 'scene' ? `scene-manual-${scene.id}` : 'scene-panel-manual'}>
        {scene.textSource === 'SCREENPLAY' ? 'Split off' : 'Added by you'}
      </span>,
    );
  }
  if (scene.analysis === 'OUT_OF_DATE') {
    marks.push(
      <span key="ood" className="sw-mark" data-tone="outdated" data-testid={idPrefix === 'scene' ? `scene-out-of-date-${scene.id}` : 'scene-panel-out-of-date'}>
        Changed since the AI read it
      </span>,
    );
  } else if (scene.needsReading) {
    marks.push(
      <span key="unread" className="sw-mark" data-testid={idPrefix === 'scene' ? `scene-unread-${scene.id}` : 'scene-panel-unread'}>
        Not read by the AI yet
      </span>,
    );
  }

  return marks.length === 0 ? null : <>{marks}</>;
}

/**
 * ONE SCENE, IN THE DETAIL PANE — the review step 2 is for.
 *
 * Read top to bottom: which scene, how it stands and why, the screenplay's own
 * words, what was read out of them, and the one thing to do — Confirm. The
 * corrections (heading, split, merge, remove) sit beside it as secondary
 * actions; the rarer ones (move, add a scene after) are one menu away. A
 * correction is saved when it is made; there is nothing to "save" at the end.
 *
 * THE SCREENPLAY IS NOT EDITED HERE. A parsed scene's text is the uploaded
 * file's and is shown read-only; correcting a heading keeps the parsed one beside
 * it ("As written"). Only a scene a person added has text a person can type —
 * the owner request of 2026-10-29 — and it is labelled as typed.
 */
export function ScenePanel({
  scene,
  position,
  nextScene,
  mergedInto,
  hasPrevious,
  hasNext,
  canMoveUp,
  canMoveDown,
  nextFlagged,
  busy,
  error,
  refusal,
  focusOnMount = false,
  discuss,
  onPrevious,
  onNext,
  onNextFlagged,
  onConfirm,
  onSave,
  onSplit,
  onMerge,
  onExclude,
  onRestore,
  onMove,
  onAddAfter,
}: {
  scene: SceneValidationRow;
  /** Where it sits in the list on screen: "12 of 93". */
  position: { index: number; total: number } | null;
  /** The scene after this one in the screenplay — what a merge would absorb. */
  nextScene: SceneValidationRow | null;
  /** For a merged scene: the one it went into. */
  mergedInto: SceneValidationRow | null;
  hasPrevious: boolean;
  hasNext: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** The next scene that needs attention, when there is another one. */
  nextFlagged: SceneValidationRow | null;
  busy: boolean;
  error: string | null;
  /** Why this person cannot correct the reading, or null when they can. */
  refusal: string | null;
  /** Take the keyboard as it opens — after a confirm moved here, not after a click in the list. */
  focusOnMount?: boolean;
  /** The scene's conversations control, when there is one. */
  discuss?: ReactNode;
  onPrevious: () => void;
  onNext: () => void;
  onNextFlagged: () => void;
  onConfirm: (confirmed: boolean) => void;
  onSave: (edit: SceneEdit) => void;
  onSplit: (input: { at: number; slugline: string }) => void;
  onMerge: () => void;
  onExclude: () => void;
  onRestore: () => void;
  onMove: (direction: 'UP' | 'DOWN') => void;
  onAddAfter: () => void;
}) {
  const [heading, setHeading] = useState(scene.slugline ?? '');
  const [number, setNumber] = useState(scene.sceneNumber ?? '');
  const [intExt, setIntExt] = useState(scene.intExt ?? '');
  const [timeOfDay, setTimeOfDay] = useState(scene.timeOfDay ?? '');
  const [text, setText] = useState(scene.text ?? '');
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<Mode>('VIEW');
  const [splitAt, setSplitAt] = useState<number | null>(null);
  const [splitHeading, setSplitHeading] = useState('');
  const region = useRef<HTMLElement | null>(null);

  const excluded = scene.status === 'EXCLUDED';
  const typed = scene.origin === 'MANUAL' && scene.textSource !== 'SCREENPLAY';
  const locked = refusal !== null || busy;

  useEffect(() => {
    if (focusOnMount) region.current?.focus();
  }, [focusOnMount]);

  const edit: SceneEdit = {};
  // Headings are stored upper case, as the screenplay writes them.
  if (heading.trim().toUpperCase() !== (scene.slugline ?? '')) edit.slugline = heading.trim();
  if (number.trim() !== (scene.sceneNumber ?? '')) edit.sceneNumber = number.trim() === '' ? null : number.trim();
  if (intExt !== (scene.intExt ?? '')) edit.intExt = intExt === '' ? null : (intExt as SceneIntExt);
  if (timeOfDay.trim().toUpperCase() !== (scene.timeOfDay ?? '')) {
    edit.timeOfDay = timeOfDay.trim() === '' ? null : timeOfDay.trim();
  }
  if (typed && text.replace(/\s+$/, '') !== (scene.text ?? '')) edit.text = text;

  const dirty = Object.keys(edit).length > 0;
  const headingEmpty = heading.trim() === '';
  const textTooLong = text.length > MAX_SCENE_TEXT_CHARS;
  const suggested = new Set(scene.suggestedSplitOffsets);
  const lines = scene.text === null ? [] : linesWithOffsets(scene.text);

  const cancelEdit = () => {
    setHeading(scene.slugline ?? '');
    setNumber(scene.sceneNumber ?? '');
    setIntExt(scene.intExt ?? '');
    setTimeOfDay(scene.timeOfDay ?? '');
    setText(scene.text ?? '');
    setEditing(false);
  };

  const startSplit = () => {
    setMode('SPLIT');
    const first = scene.suggestedSplitOffsets[0];
    if (first !== undefined && scene.text !== null) {
      setSplitAt(first);
      setSplitHeading(lines.find((l) => l.offset === first)?.line.trim() ?? '');
    }
  };

  return (
    <article
      ref={region}
      className="sw-detail"
      aria-label={`Scene ${sceneLabel(scene)} — review`}
      tabIndex={-1}
      data-testid={`scene-panel-${scene.id}`}
      data-status={scene.status}
    >
      {/* ---- which scene, and how it stands ----------------------------- */}
      <header className="sw-detail-head">
        <div className="sw-detail-id">
          <p className="sw-detail-kicker">
            <span>{`Scene ${sceneLabel(scene)}`}</span>
            {position === null ? null : (
              <span className="sw-detail-pos" data-testid="scene-panel-position">{`${String(position.index + 1)} of ${String(position.total)}`}</span>
            )}
          </p>
          <h3 className="sw-detail-heading scene-mono" data-testid="scene-panel-title">
            {scene.slugline ?? 'Untitled scene'}
          </h3>
          <p className="sw-detail-facts">
            <span>{sceneFacts(scene)}</span>
            <SceneMarkers scene={scene} idPrefix="scene-panel" />
          </p>
        </div>
        <div className="sw-detail-tools">
          <StatusPill state={STATUS_STATE[scene.status]} label={STATUS_LABEL[scene.status]} testId="scene-panel-status" />
          {discuss}
          <span className="sw-detail-nav" role="group" aria-label="Move between scenes">
            <button type="button" className="ctl sw-icon-btn" data-testid="scene-panel-previous" aria-label="Previous scene" disabled={!hasPrevious} onClick={onPrevious}>
              <Icon name="arrowLeft" size={15} />
            </button>
            <button type="button" className="ctl sw-icon-btn" data-testid="scene-panel-next" aria-label="Next scene" disabled={!hasNext} onClick={onNext}>
              <Icon name="arrowRight" size={15} />
            </button>
          </span>
        </div>
      </header>

      {nextFlagged === null ? null : (
        <button type="button" className="sw-next-flagged" data-testid="scene-panel-next-attention" onClick={onNextFlagged}>
          {`Next scene needing attention: ${sceneLabel(nextFlagged)} — ${nextFlagged.slugline ?? 'untitled'}`}
          <Icon name="arrowRight" size={13} />
        </button>
      )}

      {excluded ? (
        <div className="sw-excluded" data-testid="scene-panel-excluded">
          <p>
            <strong>{scene.exclusionReason === 'MERGED' ? 'Merged' : 'Not a scene'}</strong>{' '}
            {scene.exclusionReason === 'MERGED'
              ? `— its text joined ${mergedInto === null ? 'another scene' : `scene ${sceneLabel(mergedInto)}`}. Split the scene to undo.`
              : '— left out of the scene list and every count. Nothing was deleted.'}
          </p>
          {scene.exclusionReason === 'NOT_A_SCENE' ? (
            <button
              type="button"
              className="btn btn-primary"
              data-testid={`scene-restore-${scene.id}`}
              disabled={locked}
              title={refusal ?? 'Put it back in the scene list'}
              onClick={onRestore}
            >
              Restore scene
            </button>
          ) : null}
        </div>
      ) : null}

      {scene.warnings.length === 0 || excluded ? null : (
        <ul className="sw-warnings" data-testid="scene-panel-warnings">
          {scene.warnings.map((w) => {
            const { title, body } = warningText(w, scene);

            return (
              <li key={w} data-testid={`scene-warning-${w}`} data-confirmed={scene.status === 'CONFIRMED' ? '1' : '0'}>
                <Icon name="alert" size={15} />
                <span>
                  <strong>{title}.</strong> {body}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* ---- the screenplay's words ------------------------------------- */}
      <section className="sw-detail-section" aria-label="Scene text">
        <p className="sw-section-label">
          {typed ? 'Scene text — typed by you' : 'Script excerpt'}
          {/* The length actually held, never a fixed figure: versions parsed before
              2026-11-03 hold 2,000 characters, later ones the whole scene. */}
          {scene.textTruncated && scene.text ? ` · first ${scene.text.length.toLocaleString('en-IE')} characters` : ''}
        </p>

        {typed && mode !== 'SPLIT' && !excluded ? (
          <>
            <textarea
              className="intake-textarea scene-mono"
              data-testid="scene-panel-text-input"
              rows={8}
              value={text}
              disabled={locked}
              placeholder="What happens in this scene — action, characters, dialogue. The AI breakdown reads this text."
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
            <span className="sw-hint">
              {textTooLong
                ? `Too long by ${(text.length - MAX_SCENE_TEXT_CHARS).toLocaleString('en-GB')} characters.`
                : 'The AI breakdown reads this text. The uploaded screenplay file is not changed.'}
            </span>
          </>
        ) : mode === 'SPLIT' ? (
          <div className="scene-text sw-excerpt" data-testid="scene-panel-split-lines">
            {lines.map(({ offset, line }, index) =>
              index === 0 || line.trim() === '' ? (
                <p key={offset} className="scene-text-line">
                  {line || ' '}
                </p>
              ) : (
                <button
                  key={offset}
                  type="button"
                  className="scene-text-line scene-split-line"
                  data-testid={`scene-split-at-${String(offset)}`}
                  data-suggested={suggested.has(offset) ? '1' : '0'}
                  data-on={splitAt === offset ? '1' : '0'}
                  aria-pressed={splitAt === offset}
                  onClick={() => {
                    setSplitAt(offset);
                    setSplitHeading(line.trim());
                  }}
                >
                  {line}
                  {suggested.has(offset) ? <span className="scene-split-hint">looks like a heading</span> : null}
                </button>
              ),
            )}
          </div>
        ) : scene.text === null ? (
          <p className="mm sw-no-text" data-testid="scene-panel-no-text">
            No text was read under this heading.
          </p>
        ) : (
          <pre className="scene-text sw-excerpt" data-testid="scene-panel-text">
            {/* The line that reads like a missed heading is marked where it
                stands, so "needs attention" points at the words. */}
            {lines.map(({ offset, line }, index) => (
              <span
                key={offset}
                className={suggested.has(offset) ? 'scene-text-suggested' : undefined}
                data-testid={suggested.has(offset) ? `scene-text-suggested-${String(offset)}` : undefined}
              >
                {line}
                {index < lines.length - 1 && !suggested.has(offset) ? '\n' : ''}
              </span>
            ))}
          </pre>
        )}
      </section>

      {/* ---- what was read out of them ---------------------------------- */}
      {excluded ? null : (
        <section className="sw-detail-section" aria-label="Scene details">
          <p className="sw-section-label">Scene details</p>
          {editing ? (
            <div className="sw-fields" data-testid="scene-panel-fields">
              <label className="sw-field sw-field-wide">
                <span className="sw-field-label">Scene heading</span>
                <input
                  className="intake-input scene-mono"
                  data-testid="scene-panel-heading"
                  value={heading}
                  disabled={locked}
                  onChange={(e) => {
                    setHeading(e.target.value);
                  }}
                />
              </label>
              <label className="sw-field">
                <span className="sw-field-label">Scene number</span>
                <input
                  className="intake-input"
                  data-testid="scene-panel-number"
                  value={number}
                  disabled={locked}
                  placeholder="—"
                  onChange={(e) => {
                    setNumber(e.target.value);
                  }}
                />
              </label>
              <label className="sw-field">
                <span className="sw-field-label">Int./Ext.</span>
                <select
                  className="intake-input"
                  data-testid="scene-panel-int-ext"
                  value={intExt}
                  disabled={locked}
                  onChange={(e) => {
                    setIntExt(e.target.value);
                  }}
                >
                  <option value="">Not stated</option>
                  {(['INT', 'EXT', 'INT_EXT'] as const).map((v) => (
                    <option key={v} value={v}>
                      {INT_EXT_LABEL[v]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="sw-field">
                <span className="sw-field-label">Time of day</span>
                <input
                  className="intake-input"
                  data-testid="scene-panel-time"
                  value={timeOfDay}
                  disabled={locked}
                  placeholder="—"
                  onChange={(e) => {
                    setTimeOfDay(e.target.value);
                  }}
                />
              </label>
            </div>
          ) : (
            <dl className="sw-facts" data-testid="scene-panel-facts">
              <div className="sw-fact-wide">
                <dt>Heading</dt>
                <dd className="scene-mono">{scene.slugline ?? '—'}</dd>
              </div>
              <div>
                <dt>Scene number</dt>
                <dd>{scene.sceneNumber ?? '—'}</dd>
              </div>
              <div>
                <dt>Int./Ext.</dt>
                <dd>{scene.intExt === null ? 'Not stated' : (INT_EXT_LABEL[scene.intExt] ?? scene.intExt)}</dd>
              </div>
              <div>
                <dt>Time of day</dt>
                <dd>{scene.timeOfDay === null ? '—' : scene.timeOfDay.charAt(0) + scene.timeOfDay.slice(1).toLowerCase()}</dd>
              </div>
            </dl>
          )}
          {scene.originalSlugline !== null && scene.originalSlugline !== scene.slugline ? (
            <p className="sw-hint" data-testid="scene-panel-original-heading">
              As written in the screenplay: <span className="scene-mono">{scene.originalSlugline}</span>
            </p>
          ) : null}

          {editing || dirty ? (
            <div className="sw-save" data-testid="scene-panel-save-row">
              <button
                type="button"
                className="ctl"
                data-testid="scene-panel-save"
                disabled={!dirty || headingEmpty || textTooLong || locked}
                title={refusal ?? undefined}
                onClick={() => {
                  onSave(edit);
                }}
              >
                {busy ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="ctl" data-testid="scene-panel-edit-cancel" disabled={busy} onClick={cancelEdit}>
                Cancel
              </button>
              {dirty ? (
                <span className="sw-hint" data-testid="scene-panel-unsaved">
                  Unsaved changes
                </span>
              ) : null}
            </div>
          ) : null}
        </section>
      )}

      {/* ---- correcting the structure, asked before it is done ------------ */}
      {mode === 'SPLIT' ? (
        <div className="sw-ask" data-testid="scene-split-confirm">
          {splitAt === null ? (
            <p className="mm">Pick the line where the new scene starts. Lines that read like a heading are marked.</p>
          ) : (
            <label className="sw-field">
              <span className="sw-field-label">Heading of the new scene</span>
              <input
                className="intake-input scene-mono"
                data-testid="scene-split-heading"
                value={splitHeading}
                onChange={(e) => {
                  setSplitHeading(e.target.value);
                }}
              />
              <span className="sw-hint">
                This scene keeps its heading, its text up to that line, and everything attached to it. The new scene
                takes the rest and is to be reviewed.
              </span>
            </label>
          )}
          <div className="sw-ask-actions">
            <button
              type="button"
              className="ctl"
              data-testid="scene-split-cancel"
              onClick={() => {
                setMode('VIEW');
                setSplitAt(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              data-testid="scene-split-submit"
              disabled={splitAt === null || splitHeading.trim() === '' || locked}
              onClick={() => {
                if (splitAt !== null) onSplit({ at: splitAt, slugline: splitHeading.trim() });
              }}
            >
              Split here
            </button>
          </div>
        </div>
      ) : mode === 'MERGE' && nextScene !== null ? (
        <div className="sw-ask" data-testid="scene-merge-confirm" role="alertdialog" aria-labelledby="scene-merge-title">
          <p id="scene-merge-title" className="sw-ask-title">
            {`Merge scene ${sceneLabel(nextScene)} — ${nextScene.slugline ?? 'untitled'} — into this one?`}
          </p>
          <p className="mm">
            Its text joins this scene, and the locations and elements on it move here. It leaves the scene list and is
            kept under Excluded as merged. To undo it, split this scene again.
          </p>
          <div className="sw-ask-actions">
            <button type="button" className="ctl" data-testid="scene-merge-cancel" onClick={() => setMode('VIEW')}>
              Keep them apart
            </button>
            <button type="button" className="btn" data-testid="scene-merge-submit" disabled={locked} onClick={onMerge}>
              Merge scenes
            </button>
          </div>
        </div>
      ) : mode === 'EXCLUDE' ? (
        <div className="sw-ask" data-testid="remove-scene-confirm" role="alertdialog" aria-labelledby="remove-scene-title">
          <p id="remove-scene-title" className="sw-ask-title">
            {`Remove scene ${sceneLabel(scene)} — ${scene.slugline ?? 'untitled'} — from the scene list?`}
          </p>
          <p className="mm">
            It leaves the scene list and every count. Nothing is deleted: it stays under Excluded with its text and can
            be restored. Locations attached to it stop covering it until then.
          </p>
          <div className="sw-ask-actions">
            <button type="button" className="ctl" data-testid="remove-scene-cancel" onClick={() => setMode('VIEW')}>
              Keep it
            </button>
            <button type="button" className="btn" data-testid="remove-scene-confirm-submit" disabled={locked} onClick={onExclude}>
              It is not a scene
            </button>
          </div>
        </div>
      ) : null}

      {error === null ? null : (
        <p className="intake-error" role="alert" data-testid="scene-panel-error">
          {error}
        </p>
      )}

      {/* ---- the one thing to do, and the corrections beside it ----------- */}
      {excluded ? null : (
        <footer className="sw-detail-foot">
          {scene.status === 'CONFIRMED' ? (
            <span className="sw-confirmed">
              <StatusPill state="CONFIRMED" label="Confirmed" />
              <button
                type="button"
                className="ctl"
                data-testid="scene-panel-unconfirm"
                disabled={locked}
                title={refusal ?? undefined}
                onClick={() => {
                  onConfirm(false);
                }}
              >
                Mark as not reviewed
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              data-testid="scene-panel-confirm"
              disabled={locked || dirty}
              title={dirty ? 'Save your changes first' : (refusal ?? undefined)}
              onClick={() => {
                onConfirm(true);
              }}
            >
              <Icon name="check" size={15} strokeWidth={2.6} />
              Confirm scene
            </button>
          )}

          <span className="sw-tools" role="group" aria-label="Correct the scene">
            <button
              type="button"
              className="ctl"
              data-testid="scene-panel-edit"
              disabled={locked || editing}
              title={refusal ?? undefined}
              onClick={() => {
                setEditing(true);
              }}
            >
              <Icon name="pencil" size={13} /> Edit heading
            </button>
            <button
              type="button"
              className="ctl"
              data-testid="scene-panel-merge"
              disabled={locked || nextScene === null}
              title={refusal ?? (nextScene === null ? 'This is the last scene' : 'One scene split in two: join the next one to it')}
              onClick={() => setMode('MERGE')}
            >
              Merge with next
            </button>
            <button
              type="button"
              className="ctl"
              data-testid="scene-panel-split"
              disabled={locked || scene.text === null || mode === 'SPLIT'}
              title={refusal ?? 'Two scenes run together: start a new one at a line'}
              onClick={startSplit}
            >
              Split…
            </button>
            <button
              type="button"
              className="ctl"
              data-testid="scene-panel-exclude"
              disabled={locked}
              title={refusal ?? 'This is not a scene'}
              onClick={() => setMode('EXCLUDE')}
            >
              Remove
            </button>
            <MoreMenu
              testId="scene-panel-more"
              label="More scene actions"
              items={[
                {
                  id: 'up',
                  label: 'Move up one place',
                  disabled: locked || !canMoveUp,
                  hint: refusal ?? undefined,
                  testId: 'scene-panel-move-up',
                  onSelect: () => {
                    onMove('UP');
                  },
                },
                {
                  id: 'down',
                  label: 'Move down one place',
                  disabled: locked || !canMoveDown,
                  hint: refusal ?? undefined,
                  testId: 'scene-panel-move-down',
                  onSelect: () => {
                    onMove('DOWN');
                  },
                },
                {
                  id: 'add',
                  label: 'Add a scene after this one',
                  disabled: locked,
                  hint: refusal ?? undefined,
                  testId: 'scene-panel-add-after',
                  onSelect: onAddAfter,
                },
              ]}
            />
          </span>
        </footer>
      )}
    </article>
  );
}
